import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserAwarePricingData } from './useUserAwarePricing';

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  bonusPrice?: number;
  monthlyEquivalent?: number;
  bonusMonthlyEquivalent?: number;
  currency: string;
  interval: string;
}

interface PricingConfig {
  monthly: {
    price: number;
    originalPrice: number;
    bonusPrice?: number;
  };
  yearly: {
    price: number;
    originalPrice: number;
    monthlyEquivalent: number;
    bonusPrice?: number;
    bonusMonthlyEquivalent?: number;
  };
}

interface BackendPricingResponse {
  region: 'BR' | 'US';
  currency: 'BRL' | 'USD';
  symbol: string;
  plans: PricingPlan[];
  error?: string;
  eligible_for_bonus?: boolean;
  bonus_discount?: number;
  bonus_expires_at?: string;
}

const convertToLegacyFormat = (plans: PricingPlan[]): PricingConfig | null => {
  const monthly = plans.find(p => p.id === 'monthly');
  const yearly = plans.find(p => p.id === 'yearly');
  
  if (!monthly || !yearly) {
    return null;
  }
  
  return {
    monthly: {
      price: monthly.price,
      originalPrice: monthly.originalPrice,
      bonusPrice: monthly.bonusPrice
    },
    yearly: {
      price: yearly.price,
      originalPrice: yearly.originalPrice,
      monthlyEquivalent: yearly.monthlyEquivalent || 0,
      bonusPrice: yearly.bonusPrice,
      bonusMonthlyEquivalent: yearly.bonusMonthlyEquivalent
    }
  };
};

export const useConditionalPricing = (enabled: boolean): UserAwarePricingData => {
  const [data, setData] = useState<UserAwarePricingData>({
    region: 'US',
    currency: 'USD',
    symbol: '$',
    pricing: null,
    loading: enabled, // Only mark as loading if enabled
    error: null,
    eligible_for_bonus: false,
    bonus_discount: 0,
    bonus_expires_at: null,
  });

  useEffect(() => {
    // If not enabled, return empty state immediately
    if (!enabled) {
      console.log('[CONDITIONAL-PRICING] Fetch disabled, returning empty state');
      setData({
        region: 'US',
        currency: 'USD',
        symbol: '$',
        pricing: null,
        loading: false,
        error: null,
        eligible_for_bonus: false,
        bonus_discount: 0,
        bonus_expires_at: null,
      });
      return;
    }

    const fetchPricing = async (retryCount = 0) => {
      try {
        console.log('[CONDITIONAL-PRICING] Fetching pricing (enabled)...');
        
        // Check if user is logged in or has payment session
        const storedSession = localStorage.getItem('newsletter_session');
        const paymentSession = localStorage.getItem('payment_session');
        let sessionData = null;
        let subscriptionId = null;
        
        if (storedSession) {
          sessionData = JSON.parse(storedSession);
        }
        
        if (paymentSession) {
          const paymentData = JSON.parse(paymentSession);
          subscriptionId = paymentData?.subscription_id;
        }

        // Detect campaign ref from URL and payment token
        const urlParams = new URLSearchParams(window.location.search);
        const ref = urlParams.get('ref');
        const paymentToken = urlParams.get('paymentLink') || urlParams.get('token');

        // If we have a payment token but no payment session yet, wait a bit and retry
        if (paymentToken && !paymentSession && retryCount < 3) {
          console.log('[CONDITIONAL-PRICING] Payment token detected but no session yet, retrying...', retryCount + 1);
          setTimeout(() => fetchPricing(retryCount + 1), 1000);
          return;
        }

        const { data: pricingResult, error } = await supabase.functions.invoke('get-pricing', {
          body: { 
            session_token: sessionData?.session_token,
            subscription_id: subscriptionId,
            payment_token: paymentToken,
            ref: ref,
            page_url: window.location.href,
            page_path: window.location.pathname,
            referrer: document.referrer || null
          }
        });

        if (error) {
          throw new Error(error.message);
        }

        const backendResponse = pricingResult as BackendPricingResponse;
        const convertedPricing = convertToLegacyFormat(backendResponse.plans);
        
        if (!convertedPricing) {
          throw new Error('Preços não disponíveis no momento');
        }
        
        setData({
          region: backendResponse.region,
          currency: backendResponse.currency,
          symbol: backendResponse.symbol,
          pricing: convertedPricing,
          loading: false,
          error: backendResponse.error || null,
          eligible_for_bonus: backendResponse.eligible_for_bonus,
          bonus_discount: backendResponse.bonus_discount,
          bonus_expires_at: backendResponse.bonus_expires_at,
        });

      } catch (error: any) {
        console.error('[CONDITIONAL-PRICING] Error fetching pricing:', error);
        
        setData({
          region: 'US',
          currency: 'USD',
          symbol: '$',
          pricing: null,
          loading: false,
          error: error.message || 'Erro ao carregar preços',
        });
      }
    };

    fetchPricing();

    // Listen for localStorage changes to re-fetch pricing when payment session is created
    const handleStorageChange = (e: StorageEvent) => {
      if (enabled && e.key === 'payment_session' && e.newValue && !e.oldValue) {
        console.log('[CONDITIONAL-PRICING] Payment session created, re-fetching pricing...');
        setTimeout(() => fetchPricing(), 500);
      }
    };

    // Also listen for custom events from PaymentTokenHandler
    const handlePaymentSessionReady = () => {
      if (enabled) {
        console.log('[CONDITIONAL-PRICING] Payment session ready event received, re-fetching pricing...');
        setTimeout(() => fetchPricing(), 500);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('paymentSessionReady', handlePaymentSessionReady);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('paymentSessionReady', handlePaymentSessionReady);
    };
  }, [enabled]);

  return data;
};
